// =============================================================================
// Button Component
// =============================================================================

import { type ButtonHTMLAttributes, type CSSProperties, forwardRef } from 'react';
import { clsx } from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const BTN_SHADOW =
  '0rem -0.0625rem 0rem 0rem #b5b5b5 inset, 0rem 0rem 0rem 0.0625rem #0000001a inset, 0rem 0.03125rem 0rem 0.09375rem #FFF inset';
const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500',
  secondary:
    'bg-surface-secondary text-text-primary hover:bg-surface-tertiary focus-visible:ring-brand-500',
  outline:
    'bg-white text-text-primary hover:bg-surface-secondary focus-visible:ring-brand-500',
  ghost:
    'bg-transparent text-text-secondary hover:bg-surface-secondary hover:text-text-primary focus-visible:ring-brand-500 !shadow-none',
  danger:
    '!bg-red-600 text-white hover:!bg-red-700 focus-visible:ring-red-500',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-6 py-2 text-xs gap-1.5',
  md: 'px-6 py-2 text-sm gap-2',
  lg: 'px-6 py-2.5 text-base gap-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const isGhost = variant === 'ghost';
    const isPrimary = variant === 'primary';
    const isDanger = variant === 'danger';
    const skipShadow = isGhost || isPrimary || isDanger;

    const btnStyle: CSSProperties = {
      borderRadius: '0.5rem',
      boxShadow: skipShadow ? 'none' : BTN_SHADOW,
      cursor: 'pointer',
      ...(isDanger && { backgroundColor: '#dc2626' }),
      ...style,
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        style={btnStyle}
        className={clsx(
          'btn-press inline-flex items-center justify-center font-medium cursor-pointer',
          'transition-all duration-150 ease-in-out',
          'hover:brightness-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
